// ===========================================
// TIPOS: Sistema de Roles y Permisos
// ===========================================

// Roles legacy (roles_usuario) - DEPRECADO, usar RolMultiTenant
export type Rol = 'contador_principal' | 'contador_asociado' | 'asistente' | 'solo_lectura'

// Roles del sistema multi-tenant (usuario_organizaciones)
export type RolMultiTenant = 'owner' | 'admin' | 'profesional' | 'asistente' | 'solo_lectura'

// Mapeo de roles multi-tenant a roles legacy (para reutilizar permisos)
export const ROL_MULTITENANT_TO_LEGACY: Record<RolMultiTenant, Rol> = {
  owner: 'contador_principal',
  admin: 'contador_principal',
  profesional: 'contador_asociado',
  asistente: 'asistente',
  solo_lectura: 'solo_lectura',
}

// Mapeo inverso: legacy a multi-tenant
export const ROL_LEGACY_TO_MULTITENANT: Record<Rol, RolMultiTenant> = {
  contador_principal: 'admin',
  contador_asociado: 'profesional',
  asistente: 'asistente',
  solo_lectura: 'solo_lectura',
}

// Registro de usuario con rol
export interface RolUsuario {
  id: string
  user_email: string
  user_name: string | null
  rol: Rol
  activo: boolean
  created_at: string
  updated_at: string
}

// Datos para crear/actualizar usuario
export type RolUsuarioInsert = Omit<RolUsuario, 'id' | 'created_at' | 'updated_at'>
export type RolUsuarioUpdate = Partial<Omit<RolUsuario, 'id' | 'created_at' | 'updated_at'>>

// Formulario de usuario
export interface RolUsuarioFormData {
  user_email: string
  user_name?: string
  rol: Rol
  activo?: boolean
}

// Permisos del sistema
export interface Permisos {
  // Clientes
  crearCliente: boolean
  editarCliente: boolean
  editarHonorariosCliente: boolean     // servicios con precio y honorarios_mensuales del cliente
  verHonorariosCliente: boolean        // ver los servicios con precio y los honorarios del cliente
  asignarResponsablesCliente: boolean  // quiénes son los responsables del cliente
  eliminarCliente: boolean
  verClientes: boolean
  enviarEstadoCuenta: boolean     // ORR-200: enviar/marcar-enviado estado de cuenta por mail (acción de contador, no asistente). Reemplaza el proxy editarCliente de B1.
  enviarRecibo: boolean           // ORR-336: enviar al cliente el recibo de un cobro. Mismo criterio que enviarEstadoCuenta: es acción de contador, no de asistente.

  // Movimientos
  crearMovimiento: boolean
  editarMovimiento: boolean
  eliminarMovimiento: boolean
  verMovimientos: boolean
  generarLoteMovimientos: boolean

  // Facturación electrónica (ORR-255). Solo el contador principal: la factura tiene montos de
  // cobranza, que desde ORR-524/ORR-613 son del principal. Un override por organización puede
  // habilitarlos a otro rol.
  verFacturas: boolean
  emitirFactura: boolean
  anularFactura: boolean        // nota de crédito que anula una factura autorizada

  // Cuentas y Gastos Internos del Estudio (ORR-121) — solo contador_principal
  verCuentas: boolean
  crearGasto: boolean
  editarGasto: boolean
  eliminarGasto: boolean
  gestionarCategoriasGasto: boolean

  // Presupuestos
  crearPresupuesto: boolean
  editarPresupuesto: boolean
  eliminarPresupuesto: boolean
  verPresupuestos: boolean
  /**
   * ORR-302: único flag de esta interfaz que además lee una policy RLS
   * (`presupuestos_select_scoping_responsable`, rama `cliente_id IS NULL`), vía
   * `get_caller_puede`. Los demás solo gobiernan la UI. Si cambia acá, hay que
   * regenerar el seed (`yarn rbac:seed`) o la base queda fail-closed para el flag.
   */
  verPresupuestosSinCliente: boolean
  aceptarPresupuesto: boolean     // ORR-200 B8: aprobar presupuesto (acción de negocio, separada de editar). Asistente solo PDF.
  rechazarPresupuesto: boolean    // ORR-200 B8: rechazar presupuesto.
  enviarPresupuesto: boolean      // ORR-200 B8: enviar presupuesto por mail al cliente.

  // Tareas
  crearTarea: boolean
  editarTarea: boolean
  eliminarTarea: boolean
  verTareas: boolean
  editarTareaPropia: boolean      // ORR-200 B3: excepción-creador — editar las tareas que uno mismo creó, aunque no tenga editarTarea general.

  // Documentos
  subirDocumento: boolean
  editarDocumento: boolean        // ORR-200 B2: editar metadata de un documento ya subido (separado de subir). Se scopea por responsable en Fase 1.
  eliminarDocumento: boolean
  verDocumentos: boolean

  // Empleados
  crearEmpleado: boolean
  editarEmpleado: boolean
  eliminarEmpleado: boolean
  verEmpleados: boolean

  // Calendario/Vencimientos
  crearVencimiento: boolean
  asignarConceptos: boolean         // ORR-230: repartir los conceptos de un cliente entre sus responsables
  editarVencimiento: boolean
  descartarVencimiento: boolean   // ORR-198 A4: mandar a papelera (reversible). El asistente puede.
  restaurarVencimiento: boolean   // ORR-198 A4: restituir desde la papelera. Solo admin/contador.
  eliminarVencimiento: boolean    // ORR-200 §2.3: delete duro (espeja eliminar_vencimiento_seguro: owner/admin/profesional)
  crearEvento: boolean            // ORR-227: agendar una reunión. El asistente puede (definición del cliente).
  editarEvento: boolean           // ORR-227: reprogramar o editar una reunión ya agendada.
  eliminarEvento: boolean         // ORR-200 §2.3: espeja eliminar_evento_seguro (owner/admin/profesional)
  verCalendario: boolean

  // Servicios
  eliminarServicio: boolean       // ORR-200 §2.3: espeja eliminar_servicio_seguro (owner/admin/profesional)
  editarCatalogoServicios: boolean // ORR-623: crear, editar y activar servicios del catálogo; la RLS de servicios lo exige
  verCatalogoServicios: boolean   // ORR-204 Fase 3: true = catálogo org completo; false = solo los servicios activos de sus clientes delegados

  // Impuestos (ORR-206 Fase 3)
  verImpuestos: boolean           // ver la página /impuestos (se muestra filtrada por scoping, no se oculta)
  verCatalogoImpuestos: boolean   // true = catálogo org completo; false = solo los impuestos de sus clientes delegados
  editarCatalogoImpuestos: boolean // ORR-631: crear, editar y desactivar impuestos propios del catálogo; la RLS de catalogo_impuestos lo exige

  // Configuración
  verConfiguracion: boolean
  editarConfiguracion: boolean

  // Usuarios
  gestionarUsuarios: boolean

  // Auditoría
  verAuditoria: boolean
  exportarAuditoria: boolean

  // Reportes
  verReportes: boolean
  exportarReportes: boolean

  // AFIP / ARCA — integraciones del cliente
  verCasilleroCliente: boolean
  abrirComunicacionARCA: boolean // perfecciona la notificación legal (RG 4280/2018)
  sincronizarPadron: boolean

  // Sync de vencimientos fiscales (ORR-100) — admin de la integración ARCA/COMARB
  verSyncVencimientos: boolean      // ver /admin/sync-vencimientos
  dispararSyncManual: boolean       // botón "Sync ahora"
  toggleKillSwitch: boolean         // habilitar/deshabilitar el cron (solo superadmin via whitelist)
  marcarRevisadoSync: boolean       // marcar audit log como revisado

  // AFIP auto-delegación + Letrita A (ORR-111)
  autoDelegarAfip: boolean          // bot Playwright que entra a AFIP con clave del cliente
  letritaA: boolean                  // abrir portal AFIP "como cliente" usando clave guardada
  revocarDelegacionAfip: boolean    // revocar delegación previamente otorgada al estudio
  verAuditAfip: boolean              // ver historial de acciones AFIP del cliente
}

// Matriz de permisos por rol
export const PERMISOS_POR_ROL: Record<Rol, Permisos> = {
  contador_principal: {
    // Clientes - Acceso total
    crearCliente: true,
    editarCliente: true,
    editarHonorariosCliente: true,
    verHonorariosCliente: true,
    asignarResponsablesCliente: true,
    eliminarCliente: true,
    verClientes: true,
    enviarEstadoCuenta: true,
    enviarRecibo: true,

    // Movimientos - Acceso total
    crearMovimiento: true,
    editarMovimiento: true,
    eliminarMovimiento: true,
    verMovimientos: true,
    generarLoteMovimientos: true,

    // Facturación electrónica (ORR-255)
    verFacturas: true,
    emitirFactura: true,
    anularFactura: true,

    // Cuentas y Gastos Internos - Acceso total (único rol habilitado)
    verCuentas: true,
    crearGasto: true,
    editarGasto: true,
    eliminarGasto: true,
    gestionarCategoriasGasto: true,

    // Presupuestos - Acceso total
    crearPresupuesto: true,
    editarPresupuesto: true,
    eliminarPresupuesto: true,
    verPresupuestos: true,
    verPresupuestosSinCliente: true,
    aceptarPresupuesto: true,
    rechazarPresupuesto: true,
    enviarPresupuesto: true,

    // Tareas - Acceso total
    crearTarea: true,
    editarTarea: true,
    eliminarTarea: true,
    verTareas: true,
    editarTareaPropia: true,

    // Documentos - Acceso total
    subirDocumento: true,
    editarDocumento: true,
    eliminarDocumento: true,
    verDocumentos: true,

    // Empleados - Acceso total
    crearEmpleado: true,
    editarEmpleado: true,
    eliminarEmpleado: true,
    verEmpleados: true,

    // Calendario - Acceso total
    crearVencimiento: true,
    asignarConceptos: true,
    editarVencimiento: true,
    descartarVencimiento: true,
    restaurarVencimiento: true,
    eliminarVencimiento: true,
    crearEvento: true,
    editarEvento: true,
    eliminarEvento: true,
    verCalendario: true,

    // Servicios
    eliminarServicio: true,
    editarCatalogoServicios: true,
    verCatalogoServicios: true,

    // Impuestos
    verImpuestos: true,
    verCatalogoImpuestos: true,
    editarCatalogoImpuestos: true,

    // Configuración - Acceso total
    verConfiguracion: true,
    editarConfiguracion: true,

    // Usuarios - Solo admin
    gestionarUsuarios: true,

    // Auditoría - Acceso total
    verAuditoria: true,
    exportarAuditoria: true,

    // Reportes - Acceso total
    verReportes: true,
    exportarReportes: true,

    // AFIP / ARCA - Acceso total
    verCasilleroCliente: true,
    abrirComunicacionARCA: true,
    sincronizarPadron: true,

    // Sync de vencimientos - Acceso operacional (kill switch reservado a superadmin)
    verSyncVencimientos: true,
    dispararSyncManual: true,
    toggleKillSwitch: false,
    marcarRevisadoSync: true,

    // AFIP auto-delegación - Acción de alto privilegio (entra a AFIP del cliente)
    autoDelegarAfip: true,
    letritaA: true,
    revocarDelegacionAfip: true,
    verAuditAfip: true,
  },

  contador_asociado: {
    // Clientes - CRUD sin eliminar (ORR-200 §2.3: eliminar_cliente_seguro es
    // owner/admin-only desde siempre; la matriz decía true pero el server nunca lo permitió)
    crearCliente: true,
    editarCliente: true,
    editarHonorariosCliente: false,
    verHonorariosCliente: false,
    asignarResponsablesCliente: false,
    eliminarCliente: false,
    verClientes: true,
    enviarEstadoCuenta: false,
    enviarRecibo: false,

    // Movimientos - ORR-524: la cobranza es del contador principal; el asociado no cobra
    crearMovimiento: false,
    editarMovimiento: false,
    eliminarMovimiento: false,
    verMovimientos: false,
    generarLoteMovimientos: false,

    // Facturación electrónica (ORR-255)
    verFacturas: false,
    emitirFactura: false,
    anularFactura: false,

    // Cuentas y Gastos Internos - Sin acceso (uso interno del estudio)
    verCuentas: false,
    crearGasto: false,
    editarGasto: false,
    eliminarGasto: false,
    gestionarCategoriasGasto: false,

    // Presupuestos - CRUD sin eliminar
    crearPresupuesto: true,
    editarPresupuesto: true,
    eliminarPresupuesto: false,
    verPresupuestos: true,
    verPresupuestosSinCliente: true,
    aceptarPresupuesto: true,
    rechazarPresupuesto: true,
    enviarPresupuesto: true,

    // Tareas - CRUD completo
    crearTarea: true,
    editarTarea: true,
    eliminarTarea: true,
    verTareas: true,
    editarTareaPropia: true,

    // Documentos - Subir y ver
    subirDocumento: true,
    editarDocumento: true,
    eliminarDocumento: false,
    verDocumentos: true,

    // Empleados - CRUD sin eliminar
    crearEmpleado: true,
    editarEmpleado: true,
    eliminarEmpleado: false,
    verEmpleados: true,

    // Calendario - CRUD
    crearVencimiento: true,
    asignarConceptos: true,
    editarVencimiento: true,
    descartarVencimiento: true,
    restaurarVencimiento: true,
    eliminarVencimiento: true,
    crearEvento: true,
    editarEvento: true,
    eliminarEvento: true,
    verCalendario: true,

    // Servicios
    eliminarServicio: true,
    editarCatalogoServicios: true,
    verCatalogoServicios: true,

    // Impuestos
    verImpuestos: true,
    verCatalogoImpuestos: true,
    editarCatalogoImpuestos: true,

    // Configuración - Solo ver
    verConfiguracion: true,
    editarConfiguracion: false,

    // Usuarios - Sin acceso
    gestionarUsuarios: false,

    // Auditoría - Solo ver
    verAuditoria: true,
    exportarAuditoria: false,

    // Reportes - Sin acceso (ORR-128: solo owner/admin; se otorga puntual vía permisos_extra)
    verReportes: false,
    exportarReportes: false,

    // AFIP / ARCA - Acceso total
    verCasilleroCliente: true,
    abrirComunicacionARCA: true,
    sincronizarPadron: true,

    // Sync de vencimientos - Sin acceso (es un admin tool)
    verSyncVencimientos: false,
    dispararSyncManual: false,
    toggleKillSwitch: false,
    marcarRevisadoSync: false,

    // AFIP auto-delegación - Sólo Letrita A (operativa diaria) y audit
    autoDelegarAfip: false,
    letritaA: true,
    revocarDelegacionAfip: false,
    verAuditAfip: true,
  },

  asistente: {
    // Clientes - Crear y ver, NO editar/eliminar
    crearCliente: true,
    editarCliente: false,
    editarHonorariosCliente: false,
    verHonorariosCliente: false,
    asignarResponsablesCliente: false,
    eliminarCliente: false,
    verClientes: true,
    enviarEstadoCuenta: false,
    enviarRecibo: false,

    // Movimientos - ORR-204 Fase 3 y ORR-524: oculto del todo para el asistente (interno del estudio)
    crearMovimiento: false,
    editarMovimiento: false,
    eliminarMovimiento: false,
    verMovimientos: false,
    generarLoteMovimientos: false,

    // Facturación electrónica (ORR-255)
    verFacturas: false,
    emitirFactura: false,
    anularFactura: false,

    // Cuentas y Gastos Internos - Sin acceso (uso interno del estudio)
    verCuentas: false,
    crearGasto: false,
    editarGasto: false,
    eliminarGasto: false,
    gestionarCategoriasGasto: false,

    // Presupuestos - Solo ver (asistente exporta PDF; aceptar/rechazar/enviar = contador)
    crearPresupuesto: false,
    editarPresupuesto: false,
    eliminarPresupuesto: false,
    verPresupuestos: true,
    verPresupuestosSinCliente: false,
    aceptarPresupuesto: false,
    rechazarPresupuesto: false,
    enviarPresupuesto: false,

    // Tareas - Crear y ver, NO editar ajenas; SÍ editar las propias (ORR-200 B3)
    crearTarea: true,
    editarTarea: false,
    eliminarTarea: false,
    verTareas: true,
    editarTareaPropia: true,

    // Documentos - Subir, ver y editar (scoped a sus clientes en Fase 1)
    subirDocumento: true,
    editarDocumento: true,
    eliminarDocumento: false,
    verDocumentos: true,

    // Empleados - Solo ver
    crearEmpleado: false,
    editarEmpleado: false,
    eliminarEmpleado: false,
    verEmpleados: true,

    // Calendario - Ver + marcar presentado/pago/VEP + descartar (ORR-198 A4)
    crearVencimiento: false,
    asignarConceptos: false,
    editarVencimiento: true,
    descartarVencimiento: true,
    restaurarVencimiento: false,
    eliminarVencimiento: false,
    // ORR-227: agendar reuniones sí, aunque no pueda crear vencimientos fiscales. Son cosas distintas.
    crearEvento: true,
    editarEvento: true,
    eliminarEvento: false,
    verCalendario: true,

    // Servicios - ORR-204 Fase 3: ve solo los de sus clientes delegados (no el catálogo completo)
    eliminarServicio: false,
    editarCatalogoServicios: false,
    verCatalogoServicios: false,

    // Impuestos - ORR-206 Fase 3: ve la página, filtrada a los de sus clientes
    verImpuestos: true,
    verCatalogoImpuestos: false,
    editarCatalogoImpuestos: false,

    // Configuración - Sin acceso
    verConfiguracion: false,
    editarConfiguracion: false,

    // Usuarios - Sin acceso
    gestionarUsuarios: false,

    // Auditoría - Sin acceso
    verAuditoria: false,
    exportarAuditoria: false,

    // Reportes - Sin acceso (ORR-128: el asistente no ve Reportes)
    verReportes: false,
    exportarReportes: false,

    // AFIP / ARCA - Sin acceso
    verCasilleroCliente: false,
    abrirComunicacionARCA: false,
    sincronizarPadron: false,

    // Sync de vencimientos - Sin acceso
    verSyncVencimientos: false,
    dispararSyncManual: false,
    toggleKillSwitch: false,
    marcarRevisadoSync: false,

    // AFIP auto-delegación - Sin acceso (asistente nunca toca claves AFIP)
    autoDelegarAfip: false,
    letritaA: false,
    revocarDelegacionAfip: false,
    verAuditAfip: false,
  },

  solo_lectura: {
    // Clientes - Solo ver
    crearCliente: false,
    editarCliente: false,
    editarHonorariosCliente: false,
    verHonorariosCliente: false,
    asignarResponsablesCliente: false,
    eliminarCliente: false,
    verClientes: true,
    enviarEstadoCuenta: false,
    enviarRecibo: false,

    // Movimientos - ORR-524: solo_lectura no ve la cobranza
    crearMovimiento: false,
    editarMovimiento: false,
    eliminarMovimiento: false,
    verMovimientos: false,
    generarLoteMovimientos: false,

    // Facturación electrónica (ORR-255)
    verFacturas: false,
    emitirFactura: false,
    anularFactura: false,

    // Cuentas y Gastos Internos - Sin acceso (uso interno del estudio)
    verCuentas: false,
    crearGasto: false,
    editarGasto: false,
    eliminarGasto: false,
    gestionarCategoriasGasto: false,

    // Presupuestos - Solo ver
    crearPresupuesto: false,
    editarPresupuesto: false,
    eliminarPresupuesto: false,
    verPresupuestos: true,
    verPresupuestosSinCliente: false,
    aceptarPresupuesto: false,
    rechazarPresupuesto: false,
    enviarPresupuesto: false,

    // Tareas - Solo ver
    crearTarea: false,
    editarTarea: false,
    eliminarTarea: false,
    verTareas: true,
    editarTareaPropia: false,

    // Documentos - Solo ver
    subirDocumento: false,
    editarDocumento: false,
    eliminarDocumento: false,
    verDocumentos: true,

    // Empleados - Solo ver
    crearEmpleado: false,
    editarEmpleado: false,
    eliminarEmpleado: false,
    verEmpleados: true,

    // Calendario - Solo ver
    crearVencimiento: false,
    asignarConceptos: false,
    editarVencimiento: false,
    descartarVencimiento: false,
    restaurarVencimiento: false,
    eliminarVencimiento: false,
    crearEvento: false,
    editarEvento: false,
    eliminarEvento: false,
    verCalendario: true,

    // Servicios - ORR-204 Fase 3
    eliminarServicio: false,
    editarCatalogoServicios: false,
    verCatalogoServicios: false,

    // Impuestos - ORR-206 Fase 3
    verImpuestos: true,
    verCatalogoImpuestos: false,
    editarCatalogoImpuestos: false,

    // Configuración - Sin acceso
    verConfiguracion: false,
    editarConfiguracion: false,

    // Usuarios - Sin acceso
    gestionarUsuarios: false,

    // Auditoría - Sin acceso
    verAuditoria: false,
    exportarAuditoria: false,

    // Reportes - Sin acceso (ORR-128: solo owner/admin; se otorga puntual vía permisos_extra)
    verReportes: false,
    exportarReportes: false,

    // AFIP / ARCA - Sin acceso
    verCasilleroCliente: false,
    abrirComunicacionARCA: false,
    sincronizarPadron: false,

    // Sync de vencimientos - Sin acceso
    verSyncVencimientos: false,
    dispararSyncManual: false,
    toggleKillSwitch: false,
    marcarRevisadoSync: false,

    // AFIP auto-delegación - Sin acceso
    autoDelegarAfip: false,
    letritaA: false,
    revocarDelegacionAfip: false,
    verAuditAfip: false,
  },
}

// ORR-200 Fase 2 (red-team C4): flags "meta" que permisos_extra NUNCA puede
// conceder — evita auto-escalada vía la matriz configurable. El writer de la
// matriz los rechaza y get_caller_puede() los ignora si aparecen en extra.
// Fuente única: este array genera la tabla rbac_flag_meta (scripts/gen-rbac-seed.ts).
export const FLAGS_META_NO_CONCEDIBLES: readonly (keyof Permisos)[] = [
  'gestionarUsuarios',
  'toggleKillSwitch',
  'autoDelegarAfip',
  'letritaA',
  'revocarDelegacionAfip',
  'verSyncVencimientos',
  'dispararSyncManual',
]

// ORR-626: crear implica ver. movimientos se lee con verMovimientos (verCuentas para los gastos) y un
// alta con .select() necesita leer la fila; quien edita un precio sin verlo lo pisa a ciegas.
// Fuente única: este mapa genera la tabla rbac_flag_requiere (scripts/gen-rbac-seed.ts), con la que
// actualizar_permisos_seguro y actualizar_permisos_rol_seguro cortan con 22023. Se impone al editar
// los permisos y no se deriva al leerlos: derivar concedería ver sin que nadie lo decida.
export const FLAGS_REQUIEREN: Readonly<Partial<Record<keyof Permisos, keyof Permisos>>> = {
  crearMovimiento: 'verMovimientos',
  editarMovimiento: 'verMovimientos',
  eliminarMovimiento: 'verMovimientos',
  crearGasto: 'verCuentas',
  editarGasto: 'verCuentas',
  eliminarGasto: 'verCuentas',
  editarHonorariosCliente: 'verHonorariosCliente',
  // ORR-255: emitir o anular una factura sin verlas deja al usuario sin ver lo que emitió.
  emitirFactura: 'verFacturas',
  anularFactura: 'verFacturas',
}

/**
 * Cambia un flag y arrastra los que dependen de él según FLAGS_REQUIEREN: encender concede el que
 * requiere, apagar quita los que lo requieren. Devuelve una copia y los flags arrastrados.
 */
export function aplicarRequeridos<T extends Partial<Record<keyof Permisos, boolean>>>(
  valores: T,
  flag: keyof Permisos,
  nuevo: boolean
): { valores: T; arrastrados: (keyof Permisos)[] } {
  const resultado: Partial<Record<keyof Permisos, boolean>> = { ...valores, [flag]: nuevo }
  const arrastrados: (keyof Permisos)[] = []

  if (nuevo) {
    let requerido = FLAGS_REQUIEREN[flag]
    while (requerido && !resultado[requerido]) {
      resultado[requerido] = true
      arrastrados.push(requerido)
      requerido = FLAGS_REQUIEREN[requerido]
    }
    return { valores: resultado as T, arrastrados }
  }

  const pendientes: (keyof Permisos)[] = [flag]
  while (pendientes.length > 0) {
    const actual = pendientes.pop()!
    for (const [dependiente, requerido] of Object.entries(FLAGS_REQUIEREN) as [keyof Permisos, keyof Permisos][]) {
      if (requerido !== actual || !resultado[dependiente]) continue
      resultado[dependiente] = false
      arrastrados.push(dependiente)
      pendientes.push(dependiente)
    }
  }
  return { valores: resultado as T, arrastrados }
}

// Configuración visual para roles
export const ROL_CONFIG: Record<Rol, { label: string; color: string; bgClass: string; description: string }> = {
  contador_principal: {
    label: 'Contador Principal',
    color: '#7C3AED',
    bgClass: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Acceso total: clientes con honorarios, finanzas, reportes, usuarios, configuración y ARCA.',
  },
  contador_asociado: {
    label: 'Contador Asociado',
    color: '#2563EB',
    bgClass: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Clientes (sin honorarios), tareas, calendario, presupuestos y ARCA. No ve movimientos, cuentas ni reportes.',
  },
  asistente: {
    label: 'Asistente',
    color: '#059669',
    bgClass: 'bg-green-100 text-green-800 border-green-200',
    description: 'Solo sus clientes: alta de clientes, sus tareas, documentos, marcar vencimientos y agendar reuniones.',
  },
  solo_lectura: {
    label: 'Solo Lectura',
    color: '#6B7280',
    bgClass: 'bg-gray-100 text-gray-800 border-gray-200',
    description: 'Solo ve clientes, tareas, calendario, documentos y presupuestos. No ve finanzas ni puede editar.',
  },
}

// Filtros para la lista de usuarios
export interface FiltrosUsuarios {
  rol?: Rol
  activo?: boolean
  busqueda?: string
}

// ===========================================
// SISTEMA MULTI-TENANT
// ===========================================

// Configuración visual para roles multi-tenant
export const ROL_MULTITENANT_CONFIG: Record<RolMultiTenant, {
  label: string
  color: string
  bgClass: string
  description: string
}> = {
  owner: {
    label: 'Propietario',
    color: '#DC2626',
    bgClass: 'bg-red-100 text-red-800 border-red-200',
    description: 'Dueño de la organización. Los mismos permisos que Administrador, y es el único que puede asignar o quitar el rol Propietario.',
  },
  admin: {
    label: 'Administrador',
    color: '#7C3AED',
    bgClass: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Acceso total: clientes con honorarios, finanzas, reportes, usuarios, configuración y ARCA.',
  },
  profesional: {
    label: 'Profesional',
    color: '#2563EB',
    bgClass: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Clientes (sin honorarios), tareas, calendario, presupuestos y ARCA. No ve movimientos, cuentas ni reportes.',
  },
  asistente: {
    label: 'Asistente',
    color: '#059669',
    bgClass: 'bg-green-100 text-green-800 border-green-200',
    description: 'Solo sus clientes: alta de clientes, sus tareas, documentos, marcar vencimientos y agendar reuniones.',
  },
  solo_lectura: {
    label: 'Solo Lectura',
    color: '#6B7280',
    bgClass: 'bg-gray-100 text-gray-800 border-gray-200',
    description: 'Solo ve clientes, tareas, calendario, documentos y presupuestos. No ve finanzas ni puede editar.',
  },
}

// Datos de membresía (de usuario_organizaciones)
export interface MembresiaUsuario {
  id: string
  usuario_id: string
  organizacion_id: string
  rol: RolMultiTenant
  workspaces: string[]
  permisos_extra: Partial<Permisos> | null
  permisos_denegados: (keyof Permisos)[] | null
  activo: boolean
  created_at: string
  updated_at: string
}

// ===========================================
// HELPERS DE PERMISOS
// ===========================================

/**
 * Calcula los permisos efectivos de un usuario basado en:
 * 1. Permisos base del rol
 * 2. Permisos extra (agregan permisos)
 * 3. Permisos denegados (quitan permisos)
 */
export function calcularPermisosEfectivos(
  rol: RolMultiTenant,
  permisosExtra?: Partial<Permisos> | null,
  permisosDenegados?: (keyof Permisos)[] | null,
  overridesRol?: Partial<Record<keyof Permisos, boolean>> | null
): Permisos {
  // Obtener permisos base del rol (mapeando a legacy)
  const rolLegacy = ROL_MULTITENANT_TO_LEGACY[rol]
  const permisosBase = { ...PERMISOS_POR_ROL[rolLegacy] }

  // Capa org-rol: overrides configurados para ese rol en la organización.
  // Pisan la base (conceden o deniegan). Fiel a get_caller_puede (precedencia
  // base → org-rol → extra → denegados). El espejo filtra flags meta igual que
  // el server (denylist C4), por simetría con la rama extra: un meta que se
  // colara en la tabla por SQL manual no debe pintar paneles que el server rechaza.
  if (overridesRol) {
    Object.entries(overridesRol).forEach(([key, value]) => {
      if (typeof value === 'boolean' && !FLAGS_META_NO_CONCEDIBLES.includes(key as keyof Permisos)) {
        permisosBase[key as keyof Permisos] = value
      }
    })
  }

  // Aplicar permisos extra (conceden true). NUNCA flags meta: get_caller_puede
  // los ignora en extra (denylist C4), el espejo client-side hace lo mismo.
  if (permisosExtra) {
    Object.entries(permisosExtra).forEach(([key, value]) => {
      if (value === true && !FLAGS_META_NO_CONCEDIBLES.includes(key as keyof Permisos)) {
        permisosBase[key as keyof Permisos] = true
      }
    })
  }

  // Aplicar permisos denegados (false para los listados; pisan todo)
  if (permisosDenegados && permisosDenegados.length > 0) {
    permisosDenegados.forEach((permiso) => {
      permisosBase[permiso] = false
    })
  }

  return permisosBase
}

/**
 * Retorna un objeto de permisos con todos en false
 */
export function getPermisosVacios(): Permisos {
  return {
    crearCliente: false,
    editarCliente: false,
    editarHonorariosCliente: false,
    verHonorariosCliente: false,
    asignarResponsablesCliente: false,
    eliminarCliente: false,
    verClientes: false,
    enviarEstadoCuenta: false,
    enviarRecibo: false,
    crearMovimiento: false,
    editarMovimiento: false,
    eliminarMovimiento: false,
    verMovimientos: false,
    generarLoteMovimientos: false,
    verFacturas: false,
    emitirFactura: false,
    anularFactura: false,
    verCuentas: false,
    crearGasto: false,
    editarGasto: false,
    eliminarGasto: false,
    gestionarCategoriasGasto: false,
    crearPresupuesto: false,
    editarPresupuesto: false,
    eliminarPresupuesto: false,
    verPresupuestos: false,
    verPresupuestosSinCliente: false,
    aceptarPresupuesto: false,
    rechazarPresupuesto: false,
    enviarPresupuesto: false,
    crearTarea: false,
    editarTarea: false,
    eliminarTarea: false,
    verTareas: false,
    editarTareaPropia: false,
    subirDocumento: false,
    editarDocumento: false,
    eliminarDocumento: false,
    verDocumentos: false,
    crearEmpleado: false,
    editarEmpleado: false,
    eliminarEmpleado: false,
    verEmpleados: false,
    crearVencimiento: false,
    asignarConceptos: false,
    editarVencimiento: false,
    descartarVencimiento: false,
    restaurarVencimiento: false,
    eliminarVencimiento: false,
    crearEvento: false,
    editarEvento: false,
    eliminarEvento: false,
    verCalendario: false,
    eliminarServicio: false,
    editarCatalogoServicios: false,
    verCatalogoServicios: false,
    verImpuestos: false,
    verCatalogoImpuestos: false,
    editarCatalogoImpuestos: false,
    verConfiguracion: false,
    editarConfiguracion: false,
    gestionarUsuarios: false,
    verAuditoria: false,
    exportarAuditoria: false,
    verReportes: false,
    exportarReportes: false,
    verCasilleroCliente: false,
    abrirComunicacionARCA: false,
    sincronizarPadron: false,
    verSyncVencimientos: false,
    dispararSyncManual: false,
    toggleKillSwitch: false,
    marcarRevisadoSync: false,
    autoDelegarAfip: false,
    letritaA: false,
    revocarDelegacionAfip: false,
    verAuditAfip: false,
  }
}

/**
 * Verifica si un rol tiene permisos de administrador
 */
export function esRolAdmin(rol: RolMultiTenant): boolean {
  return rol === 'owner' || rol === 'admin'
}

/**
 * Obtiene la configuración visual de un rol (soporta legacy y multi-tenant)
 */
export function getRolConfig(rol: Rol | RolMultiTenant) {
  if (rol in ROL_MULTITENANT_CONFIG) {
    return ROL_MULTITENANT_CONFIG[rol as RolMultiTenant]
  }
  return ROL_CONFIG[rol as Rol]
}
